import { useEffect, useState } from "react";
import { supabase } from "../../utils/supabaseClient";
import useRequireAdmin from "../../hooks/useRequireAdmin";
import AdminNav from "../../components/AdminNav";
import Pagination from "../../components/Pagination";
import ProductForm from "../../components/ProductForm";

const PAGE_SIZE = 10;

const escapeLike = (s) => s.replace(/[\\%_]/g, "\\$&");
const stockOf = (p) => Number(p.stock ?? 0);
const thresholdOf = (p) => Number(p.low_stock_threshold ?? 5);

function StockBadge({ product }) {
  const stock = stockOf(product);
  if (stock <= 0) return <span className="ui-badge ui-badge--out">Out of stock</span>;
  if (stock <= thresholdOf(product)) {
    return <span className="ui-badge ui-badge--low">Low: {stock}</span>;
  }
  return <span className="ui-badge ui-badge--ok">{stock}</span>;
}

export default function AdminProducts() {
  const checking = useRequireAdmin();

  // the product currently loaded into the form (null = adding a new one)
  const [editingProduct, setEditingProduct] = useState(null);

  // list
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [notice, setNotice] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // search box: wait for typing to stop, then go back to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // load the current page of products
  useEffect(() => {
    if (checking) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setListError("");

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase.from("products").select("*", { count: "exact" });
      if (debouncedSearch) query = query.ilike("name", `%${escapeLike(debouncedSearch)}%`);

      const { data, count, error } = await query
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to);

      if (cancelled) return;

      if (error) {
        if (error.code === "PGRST103" && page > 1) {
          setPage(1);
          return;
        }
        console.error(error);
        setListError("We couldn't load the products.");
        setProducts([]);
        setTotal(0);
      } else {
        setProducts(data ?? []);
        setTotal(count ?? 0);
      }
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [checking, page, debouncedSearch, reloadKey]);

  if (checking) {
    return (
      <div className="adm">
        <p className="adm-muted">Checking admin permissions...</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function startEdit(product) {
    setEditingProduct(product);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleSaved({ mode, name }) {
    setNotice(mode === "added" ? `"${name}" added.` : `"${name}" updated.`);
    setEditingProduct(null);
    if (mode === "added") setPage(1);
    setReloadKey((k) => k + 1);
  }

  async function deleteProduct(product) {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;

    setNotice("");
    setListError("");

    const { error } = await supabase.from("products").delete().eq("id", product.id);
    if (error) {
      console.error(error);
      setListError(`Could not delete "${product.name}": ${error.message}`);
      return;
    }

    if (editingProduct?.id === product.id) setEditingProduct(null);
    setNotice(`"${product.name}" deleted.`);

    if (products.length === 1 && page > 1) setPage(page - 1);
    else setReloadKey((k) => k + 1);
  }

  return (
    <div className="adm">
      <div className="adm-header">
        <h2 className="adm-title">Products</h2>
      </div>

      <AdminNav />

      {notice && <p className="ui-alert ui-alert--ok">{notice}</p>}

      <div className="adm-layout">
        {/* FORM (shared with the Add New Product page) */}
        <ProductForm
          className="adm-sticky"
          product={editingProduct}
          onSaved={handleSaved}
          onCancel={() => setEditingProduct(null)}
        />

        {/* LIST */}
        <section className="adm-card">
          <div className="adm-card-head">
            <h3>All products ({total})</h3>
            <input
              type="search"
              className="ui-input adm-search"
              placeholder="Search by name"
              aria-label="Search products by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {listError && <p className="ui-alert ui-alert--error" style={{ margin: "1rem" }}>{listError}</p>}

          {loading && products.length === 0 ? (
            <p className="adm-empty">Loading products...</p>
          ) : products.length === 0 ? (
            <p className="adm-empty">
              {debouncedSearch ? `No products match "${debouncedSearch}".` : "No products yet. Add one using the form."}
            </p>
          ) : (
            <div className="adm-table-wrap" style={{ opacity: loading ? 0.6 : 1 }}>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Country</th>
                    <th className="adm-num">Price</th>
                    <th>Stock</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="adm-cell-product">
                          {p.image_url ? (
                            <img className="adm-thumb" src={p.image_url} alt="" />
                          ) : (
                            <span className="adm-thumb adm-thumb--empty">{p.name?.charAt(0)}</span>
                          )}
                          <div>
                            <strong>{p.name}</strong>
                            <small>{p.category || "No category"}</small>
                          </div>
                        </div>
                      </td>
                      <td>{p.country || "UK"}</td>
                      <td className="adm-num">£{Number(p.price).toFixed(2)}</td>
                      <td><StockBadge product={p} /></td>
                      <td>
                        <div className="adm-actions">
                          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => startEdit(p)}>
                            Edit
                          </button>
                          <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={() => deleteProduct(p)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ padding: "0 1rem 1.25rem" }}>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </section>
      </div>
    </div>
  );
}